import { z } from 'zod';

/**
 * Discount coupons (OpenCart-style): a code the cashier/clerk applies at
 * checkout to reduce the order total. A coupon is either a `percent` of the
 * (qualifying) subtotal or a `fixed` amount in a given currency, gated by an
 * optional minimum spend, a validity window, and total / per-customer usage
 * caps. It may be restricted to specific products and/or categories — when
 * restricted, the discount applies only to matching lines (the qualifying
 * base). Application + redemption tracking land in later phases; this defines
 * the coupon and its restriction lists.
 */
const decimalString = z.string().regex(/^\d+(\.\d{1,4})?$/, 'expected non-negative decimal');
const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);

export const CouponDiscountTypeSchema = z.enum(['percent', 'fixed']);
export type CouponDiscountType = z.infer<typeof CouponDiscountTypeSchema>;

export const CouponSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  discountType: CouponDiscountTypeSchema,
  discountValue: decimalString,
  /** Currency a `fixed` coupon is denominated in (must match the sale to apply); null for `percent`. */
  currencyCode: z.string().length(3).nullable(),
  /** Minimum order subtotal required to use the coupon; null = no minimum. */
  minSubtotal: decimalString.nullable(),
  /** Cap on the computed discount amount (useful for percent coupons); null = uncapped. */
  maxDiscount: decimalString.nullable(),
  startsAt: z.string().datetime().nullable(),
  endsAt: z.string().datetime().nullable(),
  /** Max redemptions across all customers; null = unlimited. */
  usageLimit: z.number().int().nonnegative().nullable(),
  /** Max redemptions per customer; null = unlimited (only enforced when a customer is attached). */
  perCustomerLimit: z.number().int().nonnegative().nullable(),
  usedCount: z.number().int().nonnegative(),
  isActive: z.boolean(),
  /** Restrict to these items (empty = all items qualify). */
  productIds: z.array(z.string().uuid()),
  /** Restrict to these categories (empty = all categories qualify). */
  categoryIds: z.array(z.string().uuid()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Coupon = z.infer<typeof CouponSchema>;

const couponInputShape = {
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  discountType: CouponDiscountTypeSchema,
  discountValue: decimalString,
  currencyCode: currencyCode.nullable().optional(),
  minSubtotal: decimalString.nullable().optional(),
  maxDiscount: decimalString.nullable().optional(),
  startsAt: z.string().nullable().optional(),
  endsAt: z.string().nullable().optional(),
  usageLimit: z.number().int().nonnegative().nullable().optional(),
  perCustomerLimit: z.number().int().nonnegative().nullable().optional(),
  isActive: z.boolean().optional(),
  productIds: z.array(z.string().uuid()).max(1000).optional(),
  categoryIds: z.array(z.string().uuid()).max(1000).optional(),
};

/** Cross-field rules shared by create + update (only checks fields that are present). */
function refineCoupon(
  v: {
    discountType?: CouponDiscountType;
    discountValue?: string;
    currencyCode?: string | null;
    startsAt?: string | null;
    endsAt?: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (v.discountType === 'percent' && v.discountValue !== undefined) {
    const n = Number(v.discountValue);
    if (!(n > 0 && n <= 100)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['discountValue'], message: 'percent must be in (0, 100]' });
    }
  }
  if (v.discountType === 'fixed') {
    if (v.discountValue !== undefined && Number(v.discountValue) <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['discountValue'], message: 'fixed amount must be > 0' });
    }
    if (v.currencyCode === undefined || v.currencyCode === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['currencyCode'], message: 'currency is required for a fixed coupon' });
    }
  }
  if (v.startsAt && v.endsAt && new Date(v.startsAt) > new Date(v.endsAt)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endsAt'], message: 'end must be on or after start' });
  }
}

export const CouponCreateSchema = z.object(couponInputShape).superRefine(refineCoupon);
export type CouponCreate = z.infer<typeof CouponCreateSchema>;

export const CouponUpdateSchema = z.object(couponInputShape).partial().superRefine(refineCoupon);
export type CouponUpdate = z.infer<typeof CouponUpdateSchema>;

/** Body for the stateless coupon-validate/preview endpoint (document form). */
export const CouponValidateInputSchema = z.object({
  code: z.string().min(1).max(64),
  currencyCode: currencyCode,
  partyId: z.string().uuid().optional(),
  lines: z
    .array(
      z.object({
        itemId: z.string().uuid().optional(),
        // Signed to match the document line schema (credit/return lines).
        lineSubtotal: z.string().regex(/^-?\d+(\.\d{1,4})?$/),
      }),
    )
    .max(1000),
});
export type CouponValidateInput = z.infer<typeof CouponValidateInputSchema>;

/** Result of validating a coupon against an order (consumed by POS/document apply in later phases). */
export const CouponValidationSchema = z.object({
  couponId: z.string().uuid(),
  code: z.string(),
  discountType: CouponDiscountTypeSchema,
  discountValue: decimalString,
  discountAmount: decimalString,
});
export type CouponValidation = z.infer<typeof CouponValidationSchema>;

/** Where a coupon was redeemed. */
export const CouponRedemptionSourceSchema = z.enum(['pos_sale', 'invoice', 'sales_order']);
export type CouponRedemptionSource = z.infer<typeof CouponRedemptionSourceSchema>;

/** One row of the redemptions report (a coupon use that completed). */
export const CouponRedemptionSchema = z.object({
  id: z.string().uuid(),
  couponId: z.string().uuid(),
  couponCode: z.string(),
  couponName: z.string().nullable(),
  partyId: z.string().uuid().nullable(),
  partyName: z.string().nullable(),
  sourceType: CouponRedemptionSourceSchema,
  sourceId: z.string().uuid(),
  /** The source document's human number (sale/invoice/SO number), if resolvable. */
  sourceNumber: z.string().nullable(),
  discountAmount: decimalString,
  currencyCode: z.string().nullable(),
  redeemedAt: z.string().datetime(),
});
export type CouponRedemption = z.infer<typeof CouponRedemptionSchema>;
